/**
 * As Pessoas da ficha **nova** — o cartão Pessoas do "+ Empresa" (13.2, chamado de 21/10, P13).
 *
 * Antes da 13.2 o cartão só dizia "As pessoas da empresa entram depois de ela ser cadastrada", e a
 * vendedora não achava onde cadastrar o contato. Agora ele aceita linhas **antes** de salvar: as
 * mesmas quatro colunas da 2.2 (`QuatroCampos`, de `ResponsaveisDaEmpresa.tsx`), "+ Adicionar
 * pessoa" e o "×" de cada linha. Quem grava é a ficha (`FormularioDeEmpresa.tsx`): ela cria a
 * Empresa e as Pessoas de uma vez (13.14a) — este cartão é **controlado** e não grava nada.
 *
 * - Os campos ficam ligados ao `<form>` da ficha por `form=ID_DO_FORMULARIO`: o Enter numa linha
 *   envia a ficha, como em qualquer campo dela.
 * - Os `id` levam o sufixo estável da linha (`ficha-nova-<n>`, de contador): duas linhas nunca
 *   colidem, e remover uma não renomeia as outras.
 * - A recusa de cada linha (o `FALTA_NOME`, o `FALTA_TELEFONE` da 13.17 e o telefone repetido,
 *   conferidos antes; ou a mensagem
 *   **verbatim** do serviço, que diz como resolver) mora na própria linha, `role="alert"`, a linha
 *   fica **vermelha** (13.14a, DW-379) e a nomeia por `aria-describedby`.
 * - A linha cujo telefone é de um contato do WhatsApp com outro nome (`contato_com_outro_nome`,
 *   DW-388) ganha a pergunta (`PerguntaDaAdocao`); a resposta fica na linha até o próximo envio.
 * - No máximo `LIMITE_DE_LINHAS` (o teto do serviço): "+ Adicionar pessoa" para ali.
 *
 * **A Pessoa que já existe (13.19c1, item C do chamado de 26/10/2026).** O Nome sugere enquanto se
 * digita (`CampoDoNomeComSugestao`: as Pessoas que quem cadastra vê pela base). Escolhida uma, a
 * linha mostra os dados dela **só para leitura** (Nome, Cargo, Telefone e Empresa, em `<dl>`), é
 * nomeada pelo nome dela, leva o foco ao "×" que desfaz a escolha — devolvendo o que fora digitado
 * — e, quando ela já tem Empresa, diz "<nome> já é contato da Empresa <X> e passa a ser também
 * desta." (a primeira Empresa dela vista pela base; a solta não tem aviso) numa região `status`
 * **permanente** da linha, que também descreve o "×" (code review da 13.19c1). A ficha manda essa linha como
 * `{"pessoa_id": ...}`, e o serviço a liga à Empresa nova sem criar outra. A mesma Pessoa em duas
 * linhas é recusada na segunda (`PESSOA_JA_NA_LISTA`); a recusa do serviço na linha escolhida
 * aparece verbatim. Sem escolha, a linha é a Pessoa nova de sempre.
 *
 * **Paridade de celular (NFR-1):** a linha vira coluna, como as da 2.2, pela `data-largura` que a
 * ficha escreve na `.empresa` (`formulario-de-empresa.css`) — a linha só leitura também.
 */

import { useEffect, useRef } from 'react'

import type { ContatoPerguntado, PessoaSugerida } from './api'
import {
  CampoDoNomeComSugestao,
  PESSOA_JA_NA_LISTA,
  avisoDaPessoaEscolhida,
  empresaNaSugestao,
} from './CampoDoNomeComSugestao'
import { PerguntaDaAdocao, type RespostaDaAdocao, notaDaResposta } from './PerguntaDaAdocao'
import {
  ADICIONAR_PESSOA,
  type CampoDaRecusa,
  QuatroCampos,
  type Rascunho,
  TAMANHO_DO_NOME,
  semNada,
} from './ResponsaveisDaEmpresa'
import { telefoneNaTela } from './telefone-na-tela'
import './responsaveis-da-empresa.css'

/** O teto de linhas de um "+ Empresa" — espelha `LIMITE_DE_PESSOAS_NA_FICHA_NOVA` do serviço. */
export const LIMITE_DE_LINHAS = 50

/** O cargo vazio na linha só leitura. */
const VAZIO = '—'

/** Uma linha da ficha nova: a chave estável, os quatro campos, a recusa e a pergunta, quando há. */
export interface PessoaDaFichaNova {
  /** `ficha-nova-<n>` — o sufixo dos `id` e a `key` do React. */
  chave: string
  rascunho: Rascunho
  /**
   * `FALTA_NOME`, `FALTA_TELEFONE`, o telefone repetido, `PESSOA_JA_NA_LISTA`, ou a mensagem do
   * serviço que recusou esta Pessoa.
   */
  recusa: string | null
  /**
   * O campo de que a `recusa` fala (13.17): o da conferência local, ou o do **código** do serviço
   * (`campoDoCodigo`) — nunca o Nome por padrão.
   */
  campoDaRecusa: CampoDaRecusa
  /** A recusa do telefone quando o nome também falta: as duas saem na mesma rodada (13.17). */
  recusaDoTelefone: string | null
  /** O contato do WhatsApp com outro nome que o serviço perguntou (13.14a), ou `null`. */
  pergunta: ContatoPerguntado | null
  /** A resposta da pergunta, que o próximo envio leva — ou `null`. */
  resposta: RespostaDaAdocao | null
  /**
   * A Pessoa que já existe, escolhida na sugestão (13.19c1) — a linha vai como `{"pessoa_id"}` e
   * fica só leitura — ou `null`: a Pessoa nova de sempre.
   */
  escolhida: PessoaSugerida | null
}

/** A linha sem recusa nenhuma — o que editar, responder ou reenviar sem citá-la devolve. */
export const SEM_RECUSA = { recusa: null, campoDaRecusa: null, recusaDoTelefone: null } as const

/** O nome acessível de cada linha (a numeração é a da tela); a escolhida, pelo nome da Pessoa. */
export function rotuloDaLinha(posicao: number): string {
  return `Nova pessoa ${String(posicao + 1)}`
}

/**
 * A linha que vai no envio (e que conta para o D-10 e para o modal): a com a Pessoa escolhida
 * (13.19c1), ou a que tem algo digitado.
 */
export function linhaQueVai(linha: PessoaDaFichaNova): boolean {
  return linha.escolhida !== null || !linhaVazia(linha.rascunho)
}

/** A linha em branco — só espaço ou marca invisível nos quatro campos — é ignorada (D-6). */
export function linhaVazia(rascunho: Rascunho): boolean {
  const { nome, cargo, telefone, email } = rascunho
  return [nome, cargo, telefone, email].every((valor) => semNada(valor) === '')
}

/** A linha com conteúdo e sem nome: nada sai enquanto houver uma (`PESSOA_SEM_NOME`). */
export function linhaSemNome(rascunho: Rascunho): boolean {
  return !linhaVazia(rascunho) && semNada(rascunho.nome) === ''
}

/**
 * A linha com conteúdo e sem telefone (13.17): nada sai enquanto houver uma
 * (`PESSOA_SEM_TELEFONE`). A linha toda vazia continua descartada, sem recusa.
 */
export function linhaSemTelefone(rascunho: Rascunho): boolean {
  return !linhaVazia(rascunho) && semNada(rascunho.telefone) === ''
}

/**
 * Escolher uma Pessoa na linha (13.19c1). A que já está escolhida em **outra** linha é recusada
 * nesta (`PESSOA_JA_NA_LISTA`, no Nome), que continua editável com o que tinha; senão a linha
 * passa a levá-la, sem recusa nem pergunta. **O rascunho fica guardado** (code review): a linha
 * escolhida vai só com o `pessoa_id`, e desfazer devolve o que fora digitado.
 */
export function escolherNaLinha(
  linhas: readonly PessoaDaFichaNova[],
  chave: string,
  pessoa: PessoaSugerida,
): PessoaDaFichaNova[] {
  const repetida = linhas.some(
    (linha) => linha.chave !== chave && linha.escolhida?.id === pessoa.id,
  )
  return linhas.map((linha) => {
    if (linha.chave !== chave) {
      return linha
    }
    if (repetida) {
      return { ...linha, recusa: PESSOA_JA_NA_LISTA, campoDaRecusa: 'nome', recusaDoTelefone: null }
    }
    return { ...linha, ...SEM_RECUSA, pergunta: null, resposta: null, escolhida: pessoa }
  })
}

/**
 * O "×" da linha escolhida: ela volta a ser a Pessoa nova **com o que fora digitado** antes de
 * escolher (13.19c1, code review).
 */
export function desfazerAEscolha(
  linhas: readonly PessoaDaFichaNova[],
  chave: string,
): PessoaDaFichaNova[] {
  return linhas.map((linha) =>
    linha.chave === chave ? { ...linha, ...SEM_RECUSA, escolhida: null } : linha,
  )
}

/**
 * O dado só leitura da linha escolhida: termo e definição (`<dl>`), na altura da caixa
 * (`.so-leitura`) — e não um `span` com `aria-labelledby`, que o leitor de tela não lê (code review
 * da 13.19c1).
 */
function DadoSoLeitura({ id, rotulo, valor }: { id: string; rotulo: string; valor: string }) {
  return (
    <dl className="responsaveis__campo responsaveis__dado" data-campo="so-leitura">
      <dt className="responsaveis__rotulo">{rotulo}</dt>
      <dd className="responsaveis__so-leitura" id={id}>
        {valor}
      </dd>
    </dl>
  )
}

/** O foco que a tela leva depois de escolher ou desfazer — aplicado depois de a linha mudar. */
type FocoPendente = { chave: string; alvo: 'desfazer' | 'nome' } | null

export function PessoasDaFichaNova({
  linhas,
  ocupado,
  formulario,
  aoAdicionar,
  aoTrocar,
  aoDescartar,
  aoResponder,
  aoEscolher,
  aoDesfazerAEscolha,
}: {
  linhas: readonly PessoaDaFichaNova[]
  /** A cadeia de envio está em voo: linhas, "×" e "+ Adicionar pessoa" desabilitados. */
  ocupado: boolean
  /** O `id` do `<form>` da ficha. */
  formulario: string
  aoAdicionar: () => void
  aoTrocar: (chave: string, campo: keyof Rascunho, valor: string) => void
  aoDescartar: (chave: string) => void
  aoResponder: (chave: string, resposta: RespostaDaAdocao) => void
  /** A Pessoa escolhida na sugestão do Nome (13.19c1): quem monta usa `escolherNaLinha`. */
  aoEscolher: (chave: string, pessoa: PessoaSugerida) => void
  /** O "×" da linha escolhida (13.19c1): quem monta usa `desfazerAEscolha`. */
  aoDesfazerAEscolha: (chave: string) => void
}) {
  /* O foco pedido pelo clique (ou Enter) que escolheu ou desfez, levado depois que a linha mudou. */
  const focoPendente = useRef<FocoPendente>(null)
  const setFoco = (foco: FocoPendente) => {
    focoPendente.current = foco
  }
  useEffect(() => {
    const foco = focoPendente.current
    if (foco === null) {
      return
    }
    focoPendente.current = null
    const linha = linhas.find((candidata) => candidata.chave === foco.chave)
    if (linha === undefined) {
      return
    }
    /* Escolher tira o campo Nome da tela: o foco vai ao "×" que desfaz (o aviso é anunciado pela
       região da linha). A escolha recusada (a repetida) deixa o foco no Nome, onde ele está. */
    const alvo =
      foco.alvo === 'desfazer'
        ? linha.escolhida === null
          ? null
          : document.getElementById(`pessoa-desfazer-${linha.chave}`)
        : document.getElementById(`pessoa-nome-${linha.chave}`)
    alvo?.focus()
  }, [linhas])

  return (
    <div className="pessoas-da-ficha-nova">
      {linhas.length === 0 ? null : (
        <ul className="responsaveis__lista" aria-label="Pessoas a cadastrar">
          {linhas.map((linha, posicao) => {
            const idDaRecusa = `pessoa-recusa-${linha.chave}`
            const idDaRecusaDoTelefone = `pessoa-recusa-do-telefone-${linha.chave}`
            const escolhida = linha.escolhida
            const rotulo = escolhida === null ? rotuloDaLinha(posicao) : escolhida.nome
            const recusada = linha.recusa !== null || linha.recusaDoTelefone !== null
            const descricao = [
              linha.recusa === null ? null : idDaRecusa,
              linha.recusaDoTelefone === null ? null : idDaRecusaDoTelefone,
            ]
              .filter((id) => id !== null)
              .join(' ')
            const aviso = escolhida === null ? null : avisoDaPessoaEscolhida(escolhida)
            const idDoAviso = `pessoa-aviso-${linha.chave}`
            return (
              <li
                key={linha.chave}
                className={
                  recusada ? 'responsaveis__item responsaveis__item--recusada' : 'responsaveis__item'
                }
                data-recusada={recusada ? 'sim' : undefined}
                data-escolhida={escolhida === null ? undefined : 'sim'}
                role="group"
                aria-label={rotulo}
                aria-describedby={recusada ? descricao : undefined}
              >
                <div className="responsaveis__linha">
                  {escolhida === null ? (
                    <QuatroCampos
                      sufixo={linha.chave}
                      rascunho={linha.rascunho}
                      formulario={formulario}
                      desabilitado={ocupado}
                      recusaDoNome={
                        linha.recusa !== null && linha.campoDaRecusa === 'nome' ? idDaRecusa : null
                      }
                      recusaDoTelefone={
                        linha.recusaDoTelefone !== null
                          ? idDaRecusaDoTelefone
                          : linha.recusa !== null && linha.campoDaRecusa === 'telefone'
                            ? idDaRecusa
                            : null
                      }
                      aoTrocar={(campo, valor) => {
                        aoTrocar(linha.chave, campo, valor)
                      }}
                      campoDoNome={
                        <CampoDoNomeComSugestao
                          id={`pessoa-nome-${linha.chave}`}
                          valor={linha.rascunho.nome}
                          maxLength={TAMANHO_DO_NOME}
                          autoComplete={`section-pessoa-${linha.chave} name`}
                          formulario={formulario}
                          recusadoPor={
                            linha.recusa !== null && linha.campoDaRecusa === 'nome'
                              ? idDaRecusa
                              : null
                          }
                          desabilitado={ocupado}
                          aoTrocar={(valor) => {
                            aoTrocar(linha.chave, 'nome', valor)
                          }}
                          aoEscolher={(pessoa) => {
                            setFoco({ chave: linha.chave, alvo: 'desfazer' })
                            aoEscolher(linha.chave, pessoa)
                          }}
                        />
                      }
                    />
                  ) : (
                    <>
                      <DadoSoLeitura
                        id={`pessoa-nome-${linha.chave}`}
                        rotulo="Nome"
                        valor={escolhida.nome}
                      />
                      <DadoSoLeitura
                        id={`pessoa-cargo-${linha.chave}`}
                        rotulo="Cargo"
                        valor={escolhida.cargo ?? VAZIO}
                      />
                      <DadoSoLeitura
                        id={`pessoa-telefone-${linha.chave}`}
                        rotulo="Telefone"
                        valor={telefoneNaTela(escolhida.telefone)}
                      />
                      <DadoSoLeitura
                        id={`pessoa-empresa-${linha.chave}`}
                        rotulo="Empresa"
                        valor={empresaNaSugestao(escolhida)}
                      />
                    </>
                  )}
                  <div className="responsaveis__acoes">
                    {escolhida === null ? (
                      <button
                        className="responsaveis__remover"
                        type="button"
                        disabled={ocupado}
                        aria-label={`Descartar a ${rotulo.toLocaleLowerCase('pt-BR')}`}
                        title="Descartar"
                        onClick={() => {
                          aoDescartar(linha.chave)
                        }}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    ) : (
                      <button
                        id={`pessoa-desfazer-${linha.chave}`}
                        className="responsaveis__remover"
                        type="button"
                        disabled={ocupado}
                        aria-label={`Desfazer a escolha de ${escolhida.nome}`}
                        aria-describedby={aviso === null ? undefined : idDoAviso}
                        title="Desfazer a escolha"
                        onClick={() => {
                          setFoco({ chave: linha.chave, alvo: 'nome' })
                          aoDesfazerAEscolha(linha.chave)
                        }}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    )}
                  </div>
                  {linha.recusa === null ? null : (
                    /* Verbatim quando vem do serviço: é ele que diz o problema e como resolver. */
                    <p
                      className="responsaveis__erro responsaveis__erro--da-linha"
                      id={idDaRecusa}
                      role="alert"
                    >
                      {linha.recusa}
                    </p>
                  )}
                  {linha.recusaDoTelefone === null ? null : (
                    <p
                      className="responsaveis__erro responsaveis__erro--da-linha"
                      id={idDaRecusaDoTelefone}
                      role="alert"
                    >
                      {linha.recusaDoTelefone}
                    </p>
                  )}
                  {linha.pergunta === null ? null : (
                    <PerguntaDaAdocao
                      contato={linha.pergunta}
                      desabilitada={ocupado}
                      aoResponder={(resposta) => {
                        aoResponder(linha.chave, resposta)
                      }}
                    />
                  )}
                  {linha.resposta === null ? null : (
                    <p className="responsaveis__nota-da-linha" role="status">
                      {notaDaResposta(linha.resposta)}
                    </p>
                  )}
                </div>
                {/* A região do aviso é permanente: ela existe (vazia) antes de a Pessoa ser
                    escolhida, e é isso que faz o leitor de tela anunciá-la. Fora da grade, para a
                    linha vazia não ganhar o intervalo de uma fileira. */}
                <p
                  className="responsaveis__aviso-da-linha"
                  id={idDoAviso}
                  role="status"
                  data-testid="aviso-da-linha"
                >
                  {aviso ?? ''}
                </p>
              </li>
            )
          })}
        </ul>
      )}
      <div>
        <button
          className="responsaveis__acao"
          type="button"
          disabled={ocupado || linhas.length >= LIMITE_DE_LINHAS}
          onClick={aoAdicionar}
        >
          {ADICIONAR_PESSOA}
        </button>
      </div>
    </div>
  )
}
